import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Loader2, Trash2, Bell, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { getAlertRules, createAlertRule, deleteAlertRule } from '@/services/dpdpa';

const TRIGGER_TYPES = [
  { value: 'erasure_request', label: 'Erasure Request' },
  { value: 'consent_withdrawal', label: 'Consent Withdrawal' },
  { value: 'grievance_request', label: 'Grievance Filed' },
  { value: 'breach', label: 'Data Breach' },
  { value: 'access_request', label: 'Access Request' },
];

const ACTION_OPTIONS = [
  { value: 'notify_admin', label: 'Notify Admin' },
  { value: 'create_jira', label: 'Create Jira Ticket' },
];

export default function AlertRulesPage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', trigger_type: 'erasure_request', actions: ['notify_admin'], jira_enabled: false, active: true });

  const fetchRules = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getAlertRules();
      setRules(res.rules || []);
    } catch (err) {
      toast.error('Failed to load alert rules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const handleCreate = async () => {
    if (!form.name) { toast.error('Rule name is required'); return; }
    try {
      setSaving(true);
      await createAlertRule(form);
      toast.success('Alert rule created');
      setShowDialog(false);
      setForm({ name: '', trigger_type: 'erasure_request', actions: ['notify_admin'], jira_enabled: false, active: true });
      fetchRules();
    } catch (err) {
      toast.error('Failed to create rule');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this alert rule?')) return;
    try {
      await deleteAlertRule(id);
      toast.success('Rule deleted');
      fetchRules();
    } catch (err) {
      toast.error('Failed to delete rule');
    }
  };

  const toggleAction = (action) => {
    setForm(prev => {
      const actions = prev.actions.includes(action) ? prev.actions.filter(a => a !== action) : [...prev.actions, action];
      const jiraEnabled = actions.includes('create_jira');
      return { ...prev, actions, jira_enabled: jiraEnabled };
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin opacity-50" /></div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="alert-rules-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Alert Rules</h1>
          <p className="text-sm opacity-60">Automated alerts and Jira ticket creation for DPDPA events</p>
        </div>
        <Button onClick={() => setShowDialog(true)} data-testid="create-alert-rule-btn">
          <Plus className="w-4 h-4 mr-2" /> New Rule
        </Button>
      </div>

      {rules.length === 0 ? (
        <Card><CardContent className="p-8 text-center opacity-50"><Bell className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>No alert rules configured. Create rules to automate DPDPA compliance workflows.</p></CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {rules.map((rule) => (
            <Card key={rule.id} data-testid={`alert-rule-${rule.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Zap className={`w-4 h-4 ${rule.active ? 'text-amber-500' : 'text-gray-400'}`} />
                      <h3 className="font-semibold">{rule.name}</h3>
                      <Badge variant={rule.active ? 'default' : 'secondary'}>{rule.active ? 'Active' : 'Inactive'}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <Badge variant="outline" className="text-xs">
                        Trigger: {TRIGGER_TYPES.find(t => t.value === rule.trigger_type)?.label || rule.trigger_type}
                      </Badge>
                      {(rule.actions || []).map(a => (
                        <Badge key={a} variant="outline" className="text-xs capitalize">{a.replace(/_/g, ' ')}</Badge>
                      ))}
                      {rule.jira_enabled && <Badge className="text-xs bg-blue-500/10 text-blue-700">Jira Enabled</Badge>}
                    </div>
                    <div className="flex gap-4 mt-2 text-xs opacity-50">
                      <span>Triggered: {rule.triggered_count || 0} times</span>
                      {rule.last_triggered && <span>Last: {new Date(rule.last_triggered).toLocaleString()}</span>}
                      <span>Created by: {rule.created_by}</span>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(rule.id)} data-testid={`delete-rule-${rule.id}`}>
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Rule Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="create-rule-dialog">
          <DialogHeader><DialogTitle>Create Alert Rule</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Rule Name</Label>
              <Input value={form.name} onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g. Erasure Request Alert" data-testid="rule-name-input" />
            </div>
            <div>
              <Label>Trigger Event</Label>
              <Select value={form.trigger_type} onValueChange={v => setForm(prev => ({ ...prev, trigger_type: v }))}>
                <SelectTrigger data-testid="rule-trigger-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TRIGGER_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Actions</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {ACTION_OPTIONS.map(a => (
                  <Badge key={a.value} variant={form.actions.includes(a.value) ? 'default' : 'outline'} className="cursor-pointer text-xs" onClick={() => toggleAction(a.value)}>
                    {a.label}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={v => setForm(prev => ({ ...prev, active: v }))} />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="save-rule-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Create Rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
